import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  signal,
  output,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Estimate, VendorOrder } from '../../../../../shared/models/vendor-order.model';
import { QuoteCardComponent } from './quote-card/quote-card.component';
import { QuoteDetailsComponent } from './quote-details/quote-details.component';
import { QuoteProgressComponent } from './quote-progress/quote-progress.component';

const FINAL_STATUSES = new Set(['IN_PROGRESS', 'COMPLETED', 'AWAITING_AUTHORIZATION']);

@Component({
  selector: 'app-quotes',
  imports: [QuoteCardComponent, QuoteDetailsComponent, QuoteProgressComponent, CurrencyPipe, MatIconModule],
  templateUrl: './quotes.component.html',
  styleUrl: './quotes.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotesComponent {
  readonly order = input<VendorOrder>();
  readonly selectedEstimate = signal<Estimate | null>(null);
  readonly quoteConfirmed = output<string>();
  readonly previousQuotesExpanded = signal(false);

  readonly allEstimates = computed(() => this.sortedEstimates());
  readonly estimates = computed(() => this.order()?.estimates ?? []);
  readonly orderDeadline = computed(
    () => this.order()?.deadline ?? this.order()?.fixedDeadline,
  );
  readonly hasAnyEstimates = computed(() => this.estimates().length > 0);

  readonly isInProgress = computed(() => this.order()?.jobStatus === 'IN_PROGRESS');

  readonly isFinalQuoteView = computed(() => {
    const o = this.order();
    return !!o?.jobStatus && FINAL_STATUSES.has(o.jobStatus);
  });

  readonly selectedVendorEstimate = computed<Estimate | null>(() => {
    const o = this.order();
    if (!o?.vendorId || !this.isFinalQuoteView()) return null;
    return this.estimates().find(e =>
      (e.vendorId ?? e.workflow_uid) === o.vendorId
    ) ?? null;
  });

  readonly selectedVendorQuote = computed(() => {
    const est = this.selectedVendorEstimate();
    if (!est) return null;
    return est.quote?.normal ?? est.quote?.rushed ?? null;
  });

  readonly selectedCurrency = computed(() => {
    const est = this.selectedVendorEstimate();
    return est?.currency ?? this.selectedVendorQuote()?.currency ?? 'USD';
  });

  readonly selectedDeliveryType = computed(() => {
    const est = this.selectedVendorEstimate();
    if (!est) return 'Standard';
    return (est.quote?.rushed && !est.quote?.normal) ? 'Rushed' : 'Standard';
  });

  readonly selectedDeliveryDate = computed(() => {
    const est = this.selectedVendorEstimate();
    if (!est) return '--';
    const type = this.selectedDeliveryType();
    const raw = type === 'Rushed'
      ? est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
      : est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;
    return raw ? this.formatDate(raw) : (est.deadline ? this.formatDate(est.deadline) : '--');
  });

  readonly selectedQuoteId = computed(() => this.order()?.jobNumber ?? '--');

  readonly selectedCostBreakdown = computed<{ concept: string; cost: number }[]>(() => {
    const q = this.selectedVendorQuote();
    if (!q) return [];
    if (q.costs?.length) return q.costs;
    const items: { concept: string; cost: number }[] = [];
    if (q.dtpCost) items.push({ concept: 'Desktop publishing', cost: q.dtpCost });
    if (q.translationCost) items.push({ concept: 'File preparation', cost: q.translationCost });
    if (q.managementCost) items.push({ concept: 'Management', cost: q.managementCost });
    if (q.rushedFees) items.push({ concept: 'Rushed fees', cost: q.rushedFees });
    return items;
  });

  readonly selectedTotalCost = computed(() => {
    const q = this.selectedVendorQuote();
    if (q?.totalCost != null) return q.totalCost;
    const est = this.selectedVendorEstimate();
    return est?.priceMax ?? est?.priceMin ?? 0;
  });

  readonly previousEstimates = computed(() => {
    const selected = this.selectedVendorEstimate();
    if (!selected) return this.sortedEstimates();
    const selectedId = selected.vendorId ?? selected.workflow_uid;
    return this.sortedEstimates().filter(e =>
      (e.vendorId ?? e.workflow_uid) !== selectedId
    );
  });

  readonly purchaseOrderId = computed(() => this.order()?.vendorPrice ?? '--');


  readonly lowestOverallId = computed<string | null>(() => {
    const estimates = this.estimates();
    if (!estimates.length) return null;

    let lowestCost = Infinity;
    let lowestId: string | null = null;

    for (const est of estimates) {
      const cost = est.quote?.normal?.totalCost ?? est.priceMax ?? est.priceMin;
      if (cost == null) continue;
      const id = est.vendorId ?? est.estimationsId ?? null;
      if (id == null) continue;
      if (cost < lowestCost) {
        lowestCost = cost;
        lowestId = id;
      }
    }

    return lowestId;
  });

  onViewDetails(estimate: Estimate): void {
    this.selectedEstimate.set(estimate);
  }

  onBack(): void {
    this.selectedEstimate.set(null);
  }

  isLowestRate(estimate: Estimate): boolean {
    const cost = estimate.quote?.normal?.totalCost ?? estimate.priceMax ?? estimate.priceMin;
    if (!cost) return false;
    const id = estimate.vendorId ?? estimate.estimationsId ?? null;
    if (id == null) return false;
    return id === this.lowestOverallId();
  }

  togglePreviousQuotes(): void {
    this.previousQuotesExpanded.update(v => !v);
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }

  private readonly sortedEstimates = computed(() => {
    const estimates = [...this.estimates()];
    estimates.sort((a, b) => {
      const costA = a.quote
        ? (a.quote.normal?.totalCost ?? Infinity)
        : (a.priceMax ?? Infinity);
      const costB = b.quote
        ? (b.quote.normal?.totalCost ?? Infinity)
        : (b.priceMax ?? Infinity);
      return costA - costB;
    });
    return estimates;
  });
}
